/**
 * Vimsottari Dasha Calculator
 * Calculates sub-periods on-demand for any level (1-6)
 */

import {
  DashaPeriod,
  MahadasaPeriod,
  getSequenceFrom,
  getPlanetYears,
  getCacheKey,
  isDateInPeriod,
  MAX_DASHA_LEVEL
} from '../data/VimsottariDashaData';

/**
 * Calculate sub-periods for a parent period
 * Universal formula: sub_duration = (parent_duration × planet_years) / 120
 */
export function calculateSubPeriods(parent: DashaPeriod): DashaPeriod[] {
  if (parent.level >= MAX_DASHA_LEVEL) {
    return []; // Deha is the deepest level
  }

  const subPeriods: DashaPeriod[] = [];
  const lastPlanet = parent.planets[parent.planets.length - 1];
  const sequence = getSequenceFrom(lastPlanet);

  let currentDate = new Date(parent.startDate);

  for (const planet of sequence) {
    const planetYears = getPlanetYears(planet);
    const durationDays = (parent.durationDays * planetYears) / 120;

    const endDate = new Date(currentDate.getTime() + durationDays * 24 * 60 * 60 * 1000);

    subPeriods.push({
      planets: [...parent.planets, planet],
      startDate: new Date(currentDate),
      endDate,
      durationDays,
      level: parent.level + 1,
    });

    currentDate = endDate;
  }

  return subPeriods;
}

/**
 * Convert MahadasaPeriod from API to DashaPeriod
 */
export function mahadasaToDashaPeriod(mahadasa: MahadasaPeriod): DashaPeriod {
  return {
    planets: [mahadasa.planet],
    startDate: new Date(mahadasa.start_date),
    endDate: new Date(mahadasa.end_date),
    durationDays: mahadasa.actual_days,
    level: 1,
  };
}

/**
 * Find period containing target date
 */
export function findPeriodAtDate(periods: DashaPeriod[], targetDate: Date): DashaPeriod | null {
  for (const period of periods) {
    if (isDateInPeriod(targetDate, period)) {
      return period;
    }
  }
  return null;
}

/**
 * Get current dasha stack (all 6 levels) for a date
 */
export function getCurrentDashaStack(
  mahadasas: MahadasaPeriod[],
  targetDate: Date = new Date()
): DashaPeriod[] {
  const stack: DashaPeriod[] = [];

  // Level 1: Find Mahadasa
  const mahadasaPeriods = mahadasas.map(mahadasaToDashaPeriod);
  const mahadasa = findPeriodAtDate(mahadasaPeriods, targetDate);

  if (!mahadasa) return stack;
  stack.push({ ...mahadasa, isCurrent: true });

  // Levels 2-6: Calculate and find each level
  let currentPeriod = mahadasa;

  for (let level = 2; level <= MAX_DASHA_LEVEL; level++) {
    const subPeriods = calculateSubPeriods(currentPeriod);
    const found = findPeriodAtDate(subPeriods, targetDate);

    if (!found) break;

    stack.push({ ...found, isCurrent: true });
    currentPeriod = found;
  }

  return stack;
}

/**
 * Get expanded path to current period
 * Returns array of planet names for each level
 */
export function getCurrentPath(
  mahadasas: MahadasaPeriod[],
  targetDate: Date = new Date()
): string[] {
  const stack = getCurrentDashaStack(mahadasas, targetDate);
  if (stack.length === 0) return [];

  // Extract just the last planet from each level
  return stack.map(period => period.planets[period.planets.length - 1]);
}

/**
 * Mark current periods in a list
 */
export function markCurrentPeriods(
  periods: DashaPeriod[],
  targetDate: Date = new Date()
): DashaPeriod[] {
  return periods.map(period => ({
    ...period,
    isCurrent: isDateInPeriod(targetDate, period),
  }));
}

/**
 * Dasha cache manager
 */
export class DashaCacheManager {
  private cache: Map<string, DashaPeriod[]> = new Map();

  /**
   * Get or calculate sub-periods
   */
  getSubPeriods(parent: DashaPeriod): DashaPeriod[] {
    const key = getCacheKey(parent.planets);

    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }

    const subPeriods = calculateSubPeriods(parent);
    this.cache.set(key, subPeriods);

    return subPeriods;
  }

  /**
   * Clear cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  get size(): number {
    return this.cache.size;
  }
}
